import { useEffect, useRef, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import { paketAnfrageGesehen, paketWechseln } from '../data/supabaseAdapter'
import { supabase } from '../lib/supabase'
import type { Unit, UnitCategory, UnitKind } from '../data/model'
import { Badge, Button, Card, Field, Input, Select, Sheet, cx } from '../ui/kit'
import { preisJeMonat, usePaketStand, type Paket } from '../data/pakete'
import { kaufwegSichtbar } from '../lib/kaufweg'
import { ANBIETER } from '../lib/anbieter'
import AmenityPicker from './AmenityPicker'

const KINDS: UnitKind[] = ['room', 'apartment', 'house', 'pitch', 'other']

const KIND_LABEL: Record<'de' | 'en', Record<UnitKind, string>> = {
  de: {
    room: 'Zimmer', apartment: 'Ferienwohnung', house: 'Ferienhaus',
    pitch: 'Stellplatz', other: 'Sonstiges',
  },
  en: {
    room: 'Room', apartment: 'Holiday flat', house: 'Holiday house',
    pitch: 'Pitch', other: 'Other',
  },
}

const PALETTE = ['#0ea5e9', '#8b5cf6', '#f97316', '#14b8a6', '#f43f5e',
                 '#84cc16', '#eab308', '#06b6d4']

/**
 * Booking objects and their categories.
 *
 * Categories exist because landlords price by type — every Doppelzimmer costs the
 * same — and only occasionally make an exception for one unit. They are tenant-owned
 * rows rather than a fixed enum, since "Einzelzimmer" and "Ferienwohnung Typ A" are
 * the landlord's vocabulary, not something we can enumerate for every market.
 */
export function UnitsCard() {
  const { t, formatMoney, lang } = useI18n()
  const store = useStore()
  const stand = usePaketStand()
  const de = lang === 'de'
  const L = de ? 'de' : 'en'

  const [unit, setUnit] = useState<Unit | null>(null)
  const [category, setCategory] = useState<UnitCategory | null>(null)

  const property = store.db.properties[0]
  const cats = [...store.db.unit_categories].sort((a, b) => a.sort_order - b.sort_order)

  const newUnit = (): Unit => {
    const cat = cats[0]
    return {
      id: crypto.randomUUID(),
      tenant_id: store.db.tenant.id,
      property_id: property?.id ?? '',
      name: '',
      code: '',
      kind: cat?.kind ?? 'apartment',
      category_id: cat?.id ?? null,
      max_occupancy: cat?.default_max_occupancy ?? 2,
      base_occupancy: cat?.default_occupancy ?? 2,
      base_price: cat?.default_price ?? 0,
      extra_person_price: 0,
      min_nights: cat?.default_min_nights ?? 1,
      color: PALETTE[store.db.units.length % PALETTE.length]!,
      description: {},
      amenities: [],
      sort_order: store.db.units.length,
      active: true,
    }
  }

  const newCategory = (): UnitCategory => ({
    id: crypto.randomUUID(),
    tenant_id: store.db.tenant.id,
    name: '',
    kind: 'room',
    default_occupancy: 2,
    default_max_occupancy: 2,
    default_price: 0,
    default_min_nights: 1,
    color: PALETTE[cats.length % PALETTE.length]!,
    sort_order: cats.length,
    active: true,
  })

  const unitsByCategory = (id: string | null) =>
    store.db.units.filter((u) => (u.category_id ?? null) === id)

  const uncategorised = unitsByCategory(null)

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <h2 className="font-medium flex-1">
            {de ? 'Objekte' : 'Booking objects'}
            <span className="text-muted font-normal"> · {store.db.units.length}</span>
          </h2>
          <Button size="sm" onClick={() => setCategory(newCategory())}>
            <Plus size={15} /> {de ? 'Kategorie' : 'Category'}
          </Button>
          <Button size="sm" variant="primary" disabled={!property}
            onClick={() => setUnit(newUnit())}>
            <Plus size={15} /> {de ? 'Objekt' : 'Object'}
          </Button>
        </div>
        <p className="text-xs text-muted">
          {de
            ? 'Kategorien bündeln gleichartige Objekte — der Preis wird einmal pro Kategorie gepflegt und kann für einzelne Objekte überschrieben werden.'
            : 'Categories group objects of the same type — price them once per category and override single objects where needed.'}
        </p>

        {/*
          * Der Stand steht hier, BEVOR er beim Speichern überrascht. Ein
          * Vermieter, der an seiner Grenze sitzt, soll das lesen können, statt
          * es beim nächsten Anlegeversuch zu erfahren. Kein Betrag: die Zahl
          * gehört zum Paket, nicht in diese Zeile — und in der Hülle dürfte
          * hier ohnehin keiner stehen.
          */}
        {stand.paket && stand.grenze !== null && (
          <p className="text-xs text-muted">
            {de
              ? `${stand.paket.name.de} · ${stand.einheiten} von ${stand.grenze} Einheiten`
              : `${stand.paket.name.en ?? stand.paket.name.de} · ${stand.einheiten} of ${stand.grenze} units`}
          </p>
        )}

        {cats.map((c) => {
          const list = unitsByCategory(c.id)
          return (
            <section key={c.id}>
              <button
                onClick={() => setCategory(c)}
                className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-surface-2 rounded-lg px-1"
              >
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c.color }} />
                <span className="text-sm font-medium min-w-0 flex-1 truncate">{c.name}</span>
                <Badge>{KIND_LABEL[L][c.kind]}</Badge>
                <span className="text-xs text-muted whitespace-nowrap">
                  {de ? 'ab' : 'from'} {formatMoney(c.default_price)}
                </span>
              </button>

              {list.length === 0 ? (
                <p className="text-xs text-muted pl-6 py-1">
                  {de ? 'Noch keine Objekte' : 'No objects yet'}
                </p>
              ) : (
                <ul className="pl-6 divide-y divide-border">
                  {list.map((u) => <UnitRow key={u.id} u={u} onPick={setUnit} />)}
                </ul>
              )}
            </section>
          )
        })}

        {uncategorised.length > 0 && (
          <section>
            <div className="text-sm font-medium py-1.5 text-muted">
              {de ? 'Ohne Kategorie' : 'Uncategorised'}
            </div>
            <ul className="pl-6 divide-y divide-border">
              {uncategorised.map((u) => <UnitRow key={u.id} u={u} onPick={setUnit} />)}
            </ul>
          </section>
        )}

        {store.db.units.length === 0 && (
          <p className="text-sm text-muted">{t('common.noResults')}</p>
        )}
      </Card>

      <UnitSheet unit={unit} onClose={() => setUnit(null)} />
      <CategorySheet category={category} onClose={() => setCategory(null)} />
    </>
  )
}

function UnitRow({ u, onPick }: { u: Unit; onPick: (u: Unit) => void }) {
  const { formatMoney, lang } = useI18n()
  const de = lang === 'de'
  return (
    <li>
      <button
        onClick={() => onPick(u)}
        className="w-full text-left py-2 flex items-center gap-3 hover:bg-surface-2 rounded-lg px-1"
      >
        <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: u.color }} />
        <span className="min-w-0 flex-1">
          <span className="text-sm block truncate">
            {u.name}
            {!u.active && (
              <span className="text-muted"> · {de ? 'inaktiv' : 'inactive'}</span>
            )}
          </span>
          <span className="text-xs text-muted">
            {u.code ? `${u.code} · ` : ''}
            {de ? `bis ${u.max_occupancy} Pers.` : `up to ${u.max_occupancy} guests`}
            {u.size_sqm ? ` · ${u.size_sqm} m²` : ''}
          </span>
        </span>
        <span className="tabular-nums text-sm whitespace-nowrap">
          {formatMoney(u.base_price)}
        </span>
      </button>
    </li>
  )
}

function UnitSheet({ unit, onClose }: { unit: Unit | null; onClose: () => void }) {
  const { t, lang } = useI18n()
  const store = useStore()
  const stand = usePaketStand()
  const de = lang === 'de'
  const L = de ? 'de' : 'en'
  const [draft, setDraft] = useState<Unit | null>(unit)
  const [grenze, setGrenze] = useState<'hoeherstufen' | 'gespraech' | null>(null)

  useEffect(() => { setDraft(unit); setGrenze(null) }, [unit])
  if (!draft) return null

  const set = (patch: Partial<Unit>) => setDraft({ ...draft, ...patch })
  const exists = store.db.units.some((u) => u.id === draft.id)
  const bookingCount = store.db.bookings.filter((b) => b.unit_id === draft.id).length

  /*
   * Speichern, und zwar der Reihe nach.
   *
   * Die Prüfung sitzt beim SPEICHERN und nicht am Knopf „Objekt": der Vermieter
   * hat dann schon Bezeichnung, Preis und Belegung eingetippt, und genau das
   * ist der Moment, in dem er zum Höherstufen bereit ist. Ein blankes „geht
   * nicht" verschenkt ihn und erzeugt stattdessen einen Supportvorgang.
   *
   * Der Entwurf bleibt dabei stehen. Nach dem Wechsel wird GENAU DIESE Einheit
   * gespeichert, mit allem, was schon darin steht — ein Formular, das nach
   * einem Kauf leer ist, ist die zweite Enttäuschung in derselben Minute.
   *
   * Eine BESTEHENDE Einheit läuft nie in diese Prüfung. Sie ist schon gezählt,
   * und ein Bestandskunde über seinem Band muss seine Objekte umbenennen,
   * umfärben und bebuchen können. Zugriff degradiert, er verschwindet nicht.
   */
  const speichern = async () => {
    const neu = !exists
    if (neu && stand.frei !== null && stand.frei <= 0) {
      const naechstes = stand.naechstes
      const bezahlbar = naechstes
        && preisJeMonat(naechstes, store.subscription?.contract_term ?? 'monthly') !== null
      setGrenze(bezahlbar ? 'hoeherstufen' : 'gespraech')
      return
    }
    await store.saveUnit(draft)
    onClose()
  }

  /** Adopting a category pulls in its defaults, but never overwrites a typed value. */
  const pickCategory = (id: string) => {
    const c = store.db.unit_categories.find((x) => x.id === id)
    if (!c) { set({ category_id: null }); return }
    set({
      category_id: c.id,
      kind: c.kind,
      ...(exists ? {} : {
        max_occupancy: c.default_max_occupancy,
        base_occupancy: c.default_occupancy,
        base_price: c.default_price,
        min_nights: c.default_min_nights,
      }),
    })
  }

  return (
    <>
    <GrenzSheet
      art={grenze}
      onClose={() => setGrenze(null)}
      onGewechselt={async () => {
        await store.saveUnit(draft)
        setGrenze(null)
        onClose()
        store.reload()
      }}
    />
    <Sheet
      open={!!unit && grenze === null}
      onClose={onClose}
      title={draft.name || (de ? 'Neues Objekt' : 'New object')}
      footer={
        <>
          {exists && (
            <Button
              variant="danger"
              // Deleting a unit with bookings would orphan them; the schema refuses
              // it too (on delete restrict), so do not offer it.
              disabled={bookingCount > 0}
              title={bookingCount > 0
                ? (de ? 'Hat Buchungen — stattdessen deaktivieren' : 'Has bookings — deactivate instead')
                : undefined}
              onClick={async () => { await store.deleteUnit(draft.id); onClose() }}
            >
              <Trash2 size={16} />
            </Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" className="flex-1" disabled={!draft.name.trim()}
            onClick={speichern}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-3 gap-3">
        <Field label={de ? 'Bezeichnung' : 'Name'} className="col-span-2">
          <Input autoFocus value={draft.name}
            placeholder={de ? 'Ferienwohnung Enzian' : 'Rose Room'}
            onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <Field label={de ? 'Kürzel' : 'Code'}>
          <Input value={draft.code ?? ''} placeholder="A1"
            onChange={(e) => set({ code: e.target.value })} />
        </Field>
      </div>

      <Field label={de ? 'Kategorie' : 'Category'}>
        <Select value={draft.category_id ?? ''}
          onChange={(e) => pickCategory(e.target.value)}>
          <option value="">{de ? 'Ohne Kategorie' : 'No category'}</option>
          {store.db.unit_categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </Select>
      </Field>

      <Field label={de ? 'Art' : 'Type'}>
        <Select value={draft.kind}
          onChange={(e) => set({ kind: e.target.value as UnitKind })}>
          {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[L][k]}</option>)}
        </Select>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Betten regulär' : 'Standard occupancy'}
          hint={de ? 'Ab hier Zusatzperson' : 'Extra person charged above this'}>
          <Input type="number" min={1} max={30} value={draft.base_occupancy}
            onChange={(e) => set({ base_occupancy: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Max. Personen' : 'Max guests'}>
          <Input type="number" min={1} max={30} value={draft.max_occupancy}
            onChange={(e) => set({ max_occupancy: Number(e.target.value) })} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Grundpreis / Nacht' : 'Base price / night'}
          hint={store.db.tenant.prices_include_vat === false
            ? (de ? 'Ohne Saison · zzgl. USt.' : 'When no season applies · plus VAT')
            : (de ? 'Ohne Saison · inkl. USt.' : 'When no season applies · incl. VAT')}>
          <Input type="number" step="0.01" min={0} value={draft.base_price}
            onChange={(e) => set({ base_price: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Zusatzperson / Nacht' : 'Extra person / night'}>
          <Input type="number" step="0.01" min={0} value={draft.extra_person_price}
            onChange={(e) => set({ extra_person_price: Number(e.target.value) })} />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label={de ? 'Mindestnächte' : 'Min nights'}>
          <Input type="number" min={1} value={draft.min_nights}
            onChange={(e) => set({ min_nights: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Schlafzimmer' : 'Bedrooms'}>
          <Input type="number" min={0} value={draft.bedrooms ?? ''}
            onChange={(e) => set({
              bedrooms: e.target.value === '' ? undefined : Number(e.target.value),
            })} />
        </Field>
        <Field label="m²">
          <Input type="number" min={0} value={draft.size_sqm ?? ''}
            onChange={(e) => set({
              size_sqm: e.target.value === '' ? undefined : Number(e.target.value),
            })} />
        </Field>
      </div>

      <AmenityPicker
        value={draft.amenities ?? []}
        onChange={(next) => set({ amenities: next })}
      />

      <Field label={de ? 'Farbe im Kalender' : 'Calendar colour'}>
        <div className="flex gap-2 flex-wrap">
          {PALETTE.map((c) => (
            <button
              key={c}
              onClick={() => set({ color: c })}
              aria-label={c}
              className={cx('w-9 h-9 rounded-xl border-2',
                draft.color === c ? 'border-text' : 'border-transparent')}
              style={{ background: c }}
            />
          ))}
        </div>
      </Field>

      <label className="flex items-center gap-3 text-sm">
        <input type="checkbox" className="w-4 h-4 accent-[var(--color-accent)]"
          checked={draft.active}
          onChange={(e) => set({ active: e.target.checked })} />
        <span>
          {de ? 'Aktiv' : 'Active'}
          <span className="text-muted">
            {de ? ' — inaktive Objekte erscheinen nicht im Kalender' : ' — inactive objects are hidden from the calendar'}
          </span>
        </span>
      </label>

      {exists && bookingCount > 0 && (
        <p className="text-xs text-muted">
          {de
            ? `${bookingCount} Buchungen hängen an diesem Objekt — es lässt sich deaktivieren, aber nicht löschen.`
            : `${bookingCount} bookings reference this object — it can be deactivated but not deleted.`}
        </p>
      )}
    </Sheet>
    </>
  )
}

/**
 * Die beiden Grenzen — und sie sehen mit Absicht NICHT gleich aus.
 *
 * An der Paketgrenze ist die Antwort ein Knopf und die Sache in zehn Sekunden
 * erledigt. An der Grenze der Selbstbedienung antwortet ein Mensch. Wer beide
 * gleich gestaltet, macht aus einer Qualifizierung eine Absage — und der
 * größte Interessent, den dieses Produkt bekommt, meldet sich nie wieder.
 *
 * Deshalb steht im zweiten Fall weder ein Knopf zum Höherstufen noch eine rote
 * Fehlerdarstellung, sondern eine Frage.
 *
 * WAS HIER STEHT UND WAS NOCH FEHLT (Vorgang 010, Schnitt 4a):
 *
 * Gebaut ist die Aussage, ein Weg, der nicht in die Sackgasse führt — die
 * Adresse aus dem Impressum, hinter der ein Mensch sitzt —, und die Messung:
 * beim Öffnen dieses Blattes entsteht eine Zeile in `plan_requests` mit dem
 * Zustand `seen`. Sie ist die einzige Antwort auf die Frage, wie viele Betriebe
 * überhaupt an die Decke stoßen; die Sperre selbst kann sie nicht geben, weil
 * ein `raise exception` seine eigene Transaktion zurückrollt.
 *
 * NICHT gebaut ist das Formular mit der Bestätigung „ein Mensch meldet sich bis
 * …". Das ist Schnitt 4b und gesperrt, bis im CRM benannt ist, wer die Anfrage
 * abholt und woran man sieht, dass es seit Tagen niemand getan hat. Deshalb
 * steht hier KEINE Frist: eine Zusage ohne Abnehmer ist schlimmer als
 * Schweigen.
 *
 * In der Hülle bleibt die Aussage und der Weg nach draußen fällt weg — ein Weg
 * zum Abschluss außerhalb der App ist nach 3.1.3(f) dieselbe Aufforderung wie
 * ein Preis.
 */
function GrenzSheet({
  art, onClose, onGewechselt,
}: {
  art: 'hoeherstufen' | 'gespraech' | null
  onClose: () => void
  onGewechselt: () => Promise<void>
}) {
  const { lang, formatMoney } = useI18n()
  const store = useStore()
  const stand = usePaketStand()
  const de = lang === 'de'
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  const term = store.subscription?.contract_term ?? 'monthly'
  const jetzt = stand.paket
  const naechstes = stand.naechstes
  const naechsteNummer = stand.einheiten + 1

  /*
   * Die Messung, und zwar beim ÖFFNEN — nicht beim Absenden, denn hier gibt es
   * nichts abzusenden. Festgehalten wird die Zahl, die der Betrieb BRAUCHT
   * (`naechsteNummer`), nicht die, die er heute hat: danach wird ein Angebot
   * gerechnet.
   *
   * Ein Fehler wird bewusst verschluckt. Der Vermieter steht gerade vor einer
   * Grenze; ein zweiter roter Streifen, weil eine Kennzahl nicht geschrieben
   * werden konnte, hilft ihm nicht. Doppelte Zeilen fängt die Datenbank (ein
   * eindeutiger Teilindex je Betrieb), dieses Merkzeichen spart die Rundreise.
   */
  const gemerkt = useRef(false)
  useEffect(() => {
    if (art !== 'gespraech' || gemerkt.current || !supabase) return
    gemerkt.current = true
    void paketAnfrageGesehen(supabase, store.db.tenant.id, naechsteNummer).catch(() => {})
  }, [art, naechsteNummer, store.db.tenant.id])

  const wechseln = async () => {
    if (!naechstes || !supabase) return
    setBusy(true)
    setFehler(null)
    try {
      await paketWechseln(supabase, store.db.tenant.id, naechstes.code)
      await onGewechselt()
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e))
    }
    setBusy(false)
  }

  const preisSatz = (p: Paket | null): string => {
    if (!p) return ''
    const betrag = preisJeMonat(p, term)
    if (betrag === null || !kaufwegSichtbar()) return ''
    return formatMoney(betrag, p.currency)
  }

  return (
    <Sheet
      open={art !== null}
      onClose={onClose}
      title={art === 'gespraech'
        ? (de ? 'Ein Betrieb Ihrer Größe' : 'A business of your size')
        : (de ? 'Ihr Paket ist voll' : 'Your plan is full')}
      footer={
        <>
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {de ? 'Zurück' : 'Back'}
          </Button>
          {art === 'hoeherstufen' && naechstes && (
            <Button variant="primary" className="flex-1" disabled={busy} onClick={wechseln}>
              {busy
                ? (de ? 'Wird gewechselt …' : 'Switching …')
                : (de
                  ? `${naechstes.name.de} nehmen`
                  : `Take ${naechstes.name.en ?? naechstes.name.de}`)}
            </Button>
          )}
        </>
      }
    >
      {art === 'gespraech' ? (
        <div className="space-y-3 text-sm">
          <p>
            {de
              ? 'StaySuite ist ein Produkt für kleine Privatvermieter. Die Selbstbedienung reicht so weit, wie die Pakete reichen — Ihr Betrieb liegt darüber.'
              : 'StaySuite is built for small private landlords. Self-service goes as far as the plans go — your business is above that.'}
          </p>
          <p>
            {de
              ? 'Darüber gibt es kein Listenpaket, sondern ein individuelles Angebot.'
              : 'Above that there is no list plan, but an individual offer.'}
          </p>
          <p className="font-medium">
            {de
              ? 'Warum ist ausgerechnet eine App für Privatvermieter für Ihren Betrieb interessant?'
              : 'Why is an app for private landlords the right one for your business?'}
          </p>
          {/*
            * Der Weg nach draußen — im Browser, und nur dort. Eine Aussage
            * „darüber gibt es ein individuelles Angebot" ohne jeden Weg, danach
            * zu fragen, ist schlimmer als eine Fehlermeldung: der Vermieter
            * sucht dann selbst und findet nichts. Die Adresse ist dieselbe wie
            * im Impressum; genannt wird sie ohne Frist, weil niemand eine
            * zugesagt hat.
            */}
          {kaufwegSichtbar() && (
            <p className="text-muted">
              {de
                ? `Schreiben Sie uns diese eine Frage an ${ANBIETER.mail} — mit Ihrer Einheitenzahl.`
                : `Send us that one answer at ${ANBIETER.mail}, with your number of units.`}
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          <p>
            {jetzt && de
              ? `${jetzt.name.de} deckt ${stand.grenze} Einheiten. Sie haben ${stand.einheiten}.`
              : jetzt
                ? `${jetzt.name.en ?? jetzt.name.de} covers ${stand.grenze} units. You have ${stand.einheiten}.`
                : ''}
          </p>
          <p>
            {naechstes && de
              ? `Für die ${naechsteNummer}. brauchen Sie ${naechstes.name.de}.`
              : naechstes
                ? `The ${naechsteNummer}th needs ${naechstes.name.en ?? naechstes.name.de}.`
                : ''}
          </p>
          {naechstes && jetzt && preisSatz(naechstes) && preisSatz(jetzt) && (
            <p>
              {de
                ? `${preisSatz(naechstes)} statt ${preisSatz(jetzt)} je Monat${term === 'yearly' ? ' im Jahresvertrag' : ' im Monatsabo'}, zzgl. USt.`
                : `${preisSatz(naechstes)} instead of ${preisSatz(jetzt)} per month${term === 'yearly' ? ' on the annual contract' : ' on the monthly plan'}, plus VAT.`}
            </p>
          )}
          <p className="text-muted">
            {de
              ? 'Der Wechsel gilt sofort. Ihr angefangenes Objekt bleibt erhalten und wird danach gespeichert.'
              : 'The switch takes effect immediately. The object you started stays and is saved afterwards.'}
          </p>
          {fehler && (
            <p className="bg-danger-soft text-danger rounded-xl px-3 py-2">{fehler}</p>
          )}
        </div>
      )}
    </Sheet>
  )
}

function CategorySheet({
  category, onClose,
}: { category: UnitCategory | null; onClose: () => void }) {
  const { t, lang } = useI18n()
  const store = useStore()
  const de = lang === 'de'
  const L = de ? 'de' : 'en'
  const [draft, setDraft] = useState<UnitCategory | null>(category)

  useEffect(() => setDraft(category), [category])
  if (!draft) return null

  const set = (patch: Partial<UnitCategory>) => setDraft({ ...draft, ...patch })
  const exists = store.db.unit_categories.some((c) => c.id === draft.id)
  const inUse = store.db.units.filter((u) => u.category_id === draft.id).length

  return (
    <Sheet
      open={!!category}
      onClose={onClose}
      title={draft.name || (de ? 'Neue Kategorie' : 'New category')}
      footer={
        <>
          {exists && (
            <Button variant="danger"
              onClick={async () => { await store.deleteUnitCategory(draft.id); onClose() }}>
              <Trash2 size={16} />
            </Button>
          )}
          <Button variant="secondary" className="flex-1" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" className="flex-1" disabled={!draft.name.trim()}
            onClick={async () => { await store.saveUnitCategory(draft); onClose() }}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field label={de ? 'Bezeichnung' : 'Name'}
        hint={de ? 'z. B. Doppelzimmer, Einzelzimmer, Ferienwohnung' : 'e.g. Double room, Single room, Holiday flat'}>
        <Input autoFocus value={draft.name}
          onChange={(e) => set({ name: e.target.value })} />
      </Field>

      <Field label={de ? 'Art' : 'Type'}>
        <Select value={draft.kind} onChange={(e) => set({ kind: e.target.value as UnitKind })}>
          {KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[L][k]}</option>)}
        </Select>
      </Field>

      <p className="text-xs text-muted">
        {de
          ? 'Die folgenden Werte sind Vorschläge für neue Objekte dieser Kategorie.'
          : 'The values below are suggestions for new objects in this category.'}
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label={de ? 'Betten regulär' : 'Standard occupancy'}>
          <Input type="number" min={1} max={30} value={draft.default_occupancy}
            onChange={(e) => set({ default_occupancy: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Max. Personen' : 'Max guests'}>
          <Input type="number" min={1} max={30} value={draft.default_max_occupancy}
            onChange={(e) => set({ default_max_occupancy: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Preis / Nacht' : 'Price / night'}>
          <Input type="number" step="0.01" min={0} value={draft.default_price}
            onChange={(e) => set({ default_price: Number(e.target.value) })} />
        </Field>
        <Field label={de ? 'Mindestnächte' : 'Min nights'}>
          <Input type="number" min={1} value={draft.default_min_nights}
            onChange={(e) => set({ default_min_nights: Number(e.target.value) })} />
        </Field>
      </div>

      {exists && (
        <p className="text-xs text-muted">
          {de
            ? `${inUse} Objekte in dieser Kategorie. Beim Löschen bleiben sie erhalten und stehen dann ohne Kategorie da.`
            : `${inUse} objects in this category. Deleting it keeps them, but leaves them uncategorised.`}
        </p>
      )}
    </Sheet>
  )
}
